import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useUser } from './UserContext';
import { UserLot } from '../types';

interface MultiLotContextType {
  selectedLots: UserLot[];
  primaryLotId: string;
  isMultiLotMode: boolean;
  isMobile: boolean;
  toggleLotSelection: (lot: UserLot) => void;
  setPrimaryLot: (lotId: string) => void;
  clearToSingleLot: (lotId?: string) => void;
  getSelectedLotIds: () => string[];
  getSelectedLotNames: () => Map<string, string>;
  getPrimaryLot: () => UserLot | undefined;
  getDisplayLotName: () => string;
}

const MultiLotContext = createContext<MultiLotContextType | undefined>(undefined);

interface MultiLotProviderProps {
  children: ReactNode;
}

export const MultiLotProvider: React.FC<MultiLotProviderProps> = ({ children }) => {
  const { lotId } = useParams<{ lotId: string }>();
  const location = useLocation();
  const { userLots } = useUser();
  const [selectedLots, setSelectedLots] = useState<UserLot[]>([]);
  const [isMobile, setIsMobile] = useState<boolean>(false);

  // Detect mobile devices
  useEffect(() => {
    const checkMobile = () => {
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isSmallScreen = window.innerWidth < 768;
      setIsMobile(isTouchDevice || isSmallScreen);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Initialize/update selection when lotId changes from URL
  useEffect(() => {
    if (!lotId || !userLots.length) return;

    const currentLot = userLots.find(lot => lot.lotId === lotId);
    if (!currentLot) return;

    setSelectedLots(prevSelected => {
      // If primary lot is already in selection, keep the selection
      if (prevSelected.some(lot => lot.lotId === lotId)) {
        return prevSelected;
      }
      // Otherwise, start fresh with just the primary lot
      return [currentLot];
    });
  }, [lotId, userLots]);

  useEffect(() => {
    const availableLotIds = new Set(userLots.map(lot => lot.lotId));
    setSelectedLots(prevSelected => prevSelected.filter(lot => availableLotIds.has(lot.lotId)));
  }, [userLots]);

  // Clear selection when navigating away from lot pages or on location change that changes lotId
  useEffect(() => {
    const pathLotId = location.pathname.match(/\/lot\/([^/]+)/)?.[1];
    if (!pathLotId) {
      // Not on a lot page, clear selection
      setSelectedLots([]);
    }
  }, [location.pathname]);

  const primaryLotId = lotId || '';

  const isMultiLotMode = useMemo(() => {
    return selectedLots.length > 1;
  }, [selectedLots.length]);

  const toggleLotSelection = useCallback((lot: UserLot) => {
    // Don't allow multi-select on mobile
    if (isMobile) return;

    setSelectedLots(prevSelected => {
      const isAlreadySelected = prevSelected.some(l => l.lotId === lot.lotId);

      if (isAlreadySelected) {
        // Don't allow deselecting the primary lot
        if (lot.lotId === primaryLotId) {
          return prevSelected;
        }
        // Remove from selection
        return prevSelected.filter(l => l.lotId !== lot.lotId);
      } else {
        // Add to selection
        return [...prevSelected, lot];
      }
    });
  }, [isMobile, primaryLotId]);

  const setPrimaryLot = useCallback((newPrimaryLotId: string) => {
    const newPrimaryLot = userLots.find(lot => lot.lotId === newPrimaryLotId);
    if (!newPrimaryLot) return;

    setSelectedLots(prevSelected => {
      // If new primary is already selected, just reorder
      if (prevSelected.some(lot => lot.lotId === newPrimaryLotId)) {
        return prevSelected;
      }
      // Otherwise add it to selection
      return [...prevSelected, newPrimaryLot];
    });
  }, [userLots]);

  const clearToSingleLot = useCallback((lotIdToClear?: string) => {
    const targetLotId = lotIdToClear || primaryLotId;
    const targetLot = userLots.find(lot => lot.lotId === targetLotId);

    if (targetLot) {
      setSelectedLots([targetLot]);
    } else {
      setSelectedLots([]);
    }
  }, [primaryLotId, userLots]);

  const getSelectedLotIds = useCallback(() => {
    return selectedLots.map(lot => lot.lotId);
  }, [selectedLots]);

  const getSelectedLotNames = useCallback(() => {
    const nameMap = new Map<string, string>();
    selectedLots.forEach(lot => {
      nameMap.set(lot.lotId, lot.lotName);
    });
    return nameMap;
  }, [selectedLots]);

  const getPrimaryLot = useCallback(() => {
    return selectedLots.find(lot => lot.lotId === primaryLotId);
  }, [selectedLots, primaryLotId]);

  const getDisplayLotName = useCallback(() => {
    const primaryLot = getPrimaryLot();
    const primaryName = primaryLot?.lotName || 'Lot';

    if (isMultiLotMode) {
      const additionalCount = selectedLots.length - 1;
      return `${primaryName} +${additionalCount}`;
    }

    return primaryName;
  }, [getPrimaryLot, isMultiLotMode, selectedLots.length]);

  const contextValue: MultiLotContextType = {
    selectedLots,
    primaryLotId,
    isMultiLotMode,
    isMobile,
    toggleLotSelection,
    setPrimaryLot,
    clearToSingleLot,
    getSelectedLotIds,
    getSelectedLotNames,
    getPrimaryLot,
    getDisplayLotName,
  };

  return (
    <MultiLotContext.Provider value={contextValue}>
      {children}
    </MultiLotContext.Provider>
  );
};

export const useMultiLot = (): MultiLotContextType => {
  const context = useContext(MultiLotContext);
  if (context === undefined) {
    throw new Error('useMultiLot must be used within a MultiLotProvider');
  }
  return context;
};

export default MultiLotContext;
